import { DashboardPage } from '@pflegenavi/frontend/pages/dashboard';
import {
  recurringItemRoutes,
  residentRoutes,
} from '@pflegenavi/frontend/pages/residents';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { Suspense, useEffect, useMemo } from 'react';

import * as Sentry from '@sentry/react';

import { Box, CircularProgress, styled } from '@mui/material';
import {
  useEmployeeProfilePhoenix,
  useGetNursingHomes,
  useGetPaymentProcessor,
  useNursingHomePhoenixApi,
  useUserProfileApi,
} from '@pflegenavi/frontend/api-nursing-home';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useFeatureFlag } from '@pflegenavi/frontend/feature-flags';
import { useFeatureFlagModal } from '@pflegenavi/frontend/feature-flags-web';
import { DashboardLayout } from '@pflegenavi/frontend/layout';
import {
  DevLangSwitcher,
  localeStorage,
} from '@pflegenavi/frontend/localization-web-app';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { useNursingHomeAccountingRoutes } from '@pflegenavi/frontend/pages/accountingPayments';
import { useCashManagementRoutes } from '@pflegenavi/frontend/pages/cash-management';
import {
  useNursingHomeRoutes,
  MyCardsPage,
} from '@pflegenavi/frontend/pages/nursing-home';
import { reportingRoutes } from '@pflegenavi/frontend/pages/reporting';
import { serviceProviderRoutes } from '@pflegenavi/frontend/pages/service-provider';
import { TestingModal } from '@pflegenavi/frontend/pages/testing';
import {
  IncompleteTransactionsProvider,
  receiptRoutes,
  useReceiptBatchRoutes,
} from '@pflegenavi/frontend/pages/transactions';
import { useTenantId } from '@pflegenavi/frontend/tenant';
import { shouldUseNewLogin } from '@pflegenavi/shared-frontend/authentication';
import {
  Login,
  RedirectResetPassword,
} from '@pflegenavi/shared-web/authentication';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { useSyncLocaleWithServer } from '@pflegenavi/shared/frontend';
import { PageNotFound, Text } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { SentryRoutes } from '../sentry';
import { EmployeeModeDropdown } from './components/EmployeeModeDropdown';
import { InitNursingHomePage } from './components/InitNursingHomePage';
import { SelectNursingHome } from '@pflegenavi/frontend/pages/nursing-home';
import { renderSwitchLanguageMenuItem } from './components/SwitchLanguageMenuItem';
import { DevPage } from './DevPage';
import { MenuItems } from './menu/MenuItems';
import { useEmployeeIsNursingHomeAdminOrManager } from '@pflegenavi/frontend/nursing-home-context';

export default function App(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const navigate = useNavigate();
  const location = useLocation();
  const tenant = useTenantId();

  const nursingHomeApi = useNursingHomePhoenixApi(true);
  const userProfileApi = useUserProfileApi(true);

  const employeeIsNursingHomeAdminOrManager =
    useEmployeeIsNursingHomeAdminOrManager();

  const { data: nursingHomes, isLoading: isLoadingNursingHomes } =
    useGetNursingHomes({
      enabled: nursingHomeApi !== undefined,
      refetchOnWindowFocus: false,
    });
  const { setNursingHomeList, selectedNursingHome } = useNursingHomeContext();

  const paymentProcessor = useGetPaymentProcessor();
  const { data: userProfile } = useEmployeeProfilePhoenix({
    enabled: userProfileApi !== undefined,
    refetchOnWindowFocus: false,
  });

  const convertedNursingHome = useMemo(() => {
    if (!nursingHomes) {
      return [];
    }

    return (
      nursingHomes.data?.map((nursingHome) => ({
        id: nursingHome.id,
        name: nursingHome.name,
        legalEntityId: nursingHome.legal_entity_id,
        pdfLogoId: nursingHome.pdf_logo_id,
        paymentsDisabled: nursingHome.payment_disabled,
        featureFlags: nursingHome.feature_flags,
        minPaymentAmountInCents: nursingHome.min_payment_amount,
        maxPaymentAmountInCents: nursingHome.max_payment_amount,
        minPaymentAmountInCentsEmployeeInitiated:
          nursingHome.min_payment_amount_employee_initiated,
        maxPaymentAmountInCentsEmployeeInitiated:
          nursingHome.max_payment_amount_employee_initiated,
        minResidentThresholdsValue: nursingHome.min_resident_thresholds_value,
        maxResidentThresholdsValue: nursingHome.max_resident_thresholds_value,
        legalEntity: nursingHome.legal_entity,
      })) ?? []
    );
  }, [nursingHomes]);

  useEffect(() => {
    if (!isLoadingNursingHomes && nursingHomes?.data) {
      setNursingHomeList(convertedNursingHome);
    }
  }, [
    convertedNursingHome,
    isLoadingNursingHomes,
    nursingHomes,
    setNursingHomeList,
  ]);

  const accountingRoutes = useNursingHomeAccountingRoutes();
  const receiptBatchRoutes = useReceiptBatchRoutes();
  const cashManagementRoutes = useCashManagementRoutes();
  const nursingHomeRoutes = useNursingHomeRoutes();

  useSyncLocaleWithServer({ localeStorage });

  useSetupSentryScopeWithNursingHome();

  const addFamilyMemberFeatureFlag = useFeatureFlag(
    FeatureFlag.AddFamilyMember
  );
  const importFamilyMembersFeatureFlag = useFeatureFlag(
    FeatureFlag.ImportFamilyMembers
  );

  const serviceProviderAppFeatureFlag = useFeatureFlag(
    FeatureFlag.dev_ServiceProviderApp
  );

  const { FeatureFlagModalComponent } = useFeatureFlagModal();

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon || !userProfile || !userProfile.user) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('identify', {
      name: `${userProfile.user.first_name} ${userProfile.user.last_name}`,
      email: userProfile.user.email ?? '',
      'nursing-home': selectedNursingHome?.name ?? 'Unknown',
      tenant,
      'user-id': userProfile.user.id,
      environment: window.location.hostname.startsWith('localhost')
        ? 'local'
        : tenant === 'default_tenant' || tenant === 'other_tenant'
        ? 'staging'
        : 'production',
      application: 'Nursing home web',
    });
  }, [userProfile, selectedNursingHome, tenant]);

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('event', {
      type: 'page-viewed',
      url: location.pathname,
      title: document.title,
    });
    // @ts-expect-error Beacon is untyped
    window.Beacon('suggest');
    if (location && location.pathname === '/') {
      // @ts-expect-error Beacon is untyped
      window.Beacon('navigate', '/');
    }
  }, [location.pathname, location]);

  if (!user || !userProfile) {
    if (shouldUseNewLogin()) {
      return <Login useEmailFirstLogin={true} />;
    }

    return (
      <LoadingContainer>
        <CircularProgress variant="indeterminate" />
      </LoadingContainer>
    );
  }

  return (
    <Suspense
      fallback={
        <Text color="primary" variant="h2">
          {t('')}
        </Text>
      }
    >
      {!import.meta.env.PROD && <DevLangSwitcher />}
      {FeatureFlagModalComponent}
      <IncompleteTransactionsProvider>
        <>
          <TestingModal />
          <SentryRoutes>
            <Route
              path={nhAppMainPages.WELCOME}
              element={<Navigate to={nhAppMainPages.HOME} replace />}
            />
            <Route
              path={'/reset-password/:token'}
              element={<RedirectResetPassword />}
            />
            <Route
              path={nhAppMainPages.HOME}
              element={
                <DashboardLayout
                  UnderLogoComponent={<SelectNursingHome />}
                  configKey="nh-web"
                  home={nhAppMainPages.HOME}
                  menuItems={
                    employeeIsNursingHomeAdminOrManager ? (
                      <MenuItems />
                    ) : undefined
                  }
                  helpUrl={'https://pn.helpscoutdocs.com/'}
                  renderSwitchLanguageMenuItem={renderSwitchLanguageMenuItem}
                  leftSideItems={<EmployeeModeDropdown />}
                />
              }
            >
              <Route
                path={nhAppMainPages.DASHBOARD}
                element={<DashboardPage />}
              />
              <Route path={nhAppMainPages.DEV_PAGE} element={<DevPage />} />
              {residentRoutes(
                addFamilyMemberFeatureFlag,
                importFamilyMembersFeatureFlag,
                serviceProviderAppFeatureFlag
              )}
              {recurringItemRoutes()}
              {serviceProviderRoutes()}
              {receiptRoutes()}
              <Route
                path={nhAppMainPages.HOME}
                element={<Navigate to={nhAppMainPages.DASHBOARD} replace />}
              />
              {receiptBatchRoutes}
              {cashManagementRoutes}
              {accountingRoutes}
              {paymentProcessor === 'stripe' && reportingRoutes()}
              {employeeIsNursingHomeAdminOrManager && nursingHomeRoutes}
              <Route path={nhAppMainPages.MY_CARDS} element={<MyCardsPage />} />
              <Route
                path={nhAppMainPages.NURSING_HOME_SELECT}
                element={<InitNursingHomePage />}
              />
              <Route
                path="*"
                element={
                  <PageNotFound
                    handleGoHomeClick={() => navigate(nhAppMainPages.HOME)}
                  />
                }
              />
            </Route>
          </SentryRoutes>
        </>
      </IncompleteTransactionsProvider>
    </Suspense>
  );
}

const useSetupSentryScopeWithNursingHome = () => {
  const { selectedNursingHome } = useNursingHomeContext();
  const { user } = useAuthentication();

  useEffect(() => {
    if (!selectedNursingHome || !user) {
      return;
    }

    const scope = Sentry.getCurrentScope();
    scope.setUser(
      user
        ? {
            id: user?.userId,
          }
        : null
    );
    scope.setTags({
      nursingHomeId: selectedNursingHome.id,
      nursingHomeName: selectedNursingHome.name,
    });
  }, [selectedNursingHome, user]);
};

const LoadingContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  })
);
